import { useEffect } from 'react';
import { BLOCKS } from '@constants';
import useScannerSettings from '@hooks/use-settings';
import { updateCSS } from '@utils/color-contrast-helpers';
import { focusOnElement, removeExistingFocus } from '@utils/focus-on-element';

const useScannerWizardEffects = (state, actions) => {
	const settings = useScannerSettings();

	const {
		sortedViolations,
		openIndex,
		pageURL,
		setOpenIndex,
		openedBlock,
		results,
		setViolation,
		setLoading,
	} = state;

	// Focus management when openIndex changes
	useEffect(() => {
		const items = sortedViolations[openedBlock];

		if (
			openIndex !== null &&
			sortedViolations[openedBlock]?.length &&
			openIndex < items?.length
		) {
			const element = sortedViolations[openedBlock][openIndex].node;
			focusOnElement(element);
		} else {
			removeExistingFocus();
		}
	}, [openIndex, openedBlock]);

	// Close accordions and remove highlights when returning to the main screen
	useEffect(() => {
		if (BLOCKS.main === openedBlock) {
			setOpenIndex(0);
			updateCSS('');
		}
	}, [openedBlock]);

	// compute violation count
	useEffect(() => {
		if (results?.summary?.counts) {
			const total = Object.values(sortedViolations).reduce(
				(sum, arr) => sum + arr.length,
				0,
			);

			setViolation(total);

			settings.violationsTotal = total;
		}
	}, [sortedViolations, results]);

	// initial load and scannerWizard logic
	useEffect(() => {
		actions.savePageURL();
	}, []);

	useEffect(() => {
		if (pageURL) {
			actions
				.fetchPage()
				.then(() => {
					const currentUrl = new URL(window.location.href);
					const currentUrlParam = currentUrl.searchParams.get('url');

					if (currentUrlParam !== pageURL) {
						const newUrl = new URL(window.location.href);
						newUrl.searchParams.set('url', pageURL);
						window.history.pushState({}, '', newUrl);
					}
				})
				.catch((e) => {
					console.error(e);
					setLoading(false);
				});
		}
	}, [pageURL]);

	// Handle browser back/forward navigation
	useEffect(() => {
		const handlePopState = () => {
			const fullURL = new URL(window.location.href);
			const urlParam = fullURL.searchParams.get('url');

			if (urlParam && urlParam !== pageURL) {
				actions.rescanPage(urlParam);
			}
		};

		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [pageURL]);
};

export default useScannerWizardEffects;
